import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ExpandedState,
  SortingState,
  ColumnPinningState,
} from '@tanstack/react-table'
import React, {CSSProperties, FC, useEffect, useMemo, useState} from 'react'
import {DashboardLeadsReportTable} from '../../interfaces/Dashboard'
import useSWR, { mutate } from 'swr'
import useAccessToken from '../../hooks/AccessToken'
import axios from 'axios'
import {useDataTableStore} from '../../stores/DataTableStore'
import {useIntl} from 'react-intl'
import {ta} from 'date-fns/locale'
import moment from 'moment-timezone'
import {
  currencyFormatter,
  transformDataLimits,
} from '../../functions/general'
import useUser from '../../hooks/User'
import {useCustomeStore} from '../../stores/CustomeStore'
import {useParams} from 'react-router-dom'
import {useMerchantStore} from '../../stores/MerchantStore'
import {usePipelineStore} from '../../stores/PipelineStore'
import {useSettingsStore} from '../../stores/SettingsStore'
import {useUserStore} from '../../stores/UserStore'
import clsx from 'clsx'
import {
  calculateTotal,
  customGlobalFilter,
  getCommonPinningStyles,
  getMonthsForYear,
  handleFormatData,
} from './TanStackParts/TanStackTableFn'
import { CRM_Pagination, CRM_Tbody, CRM_Thead, CustomCompanyView, ReimbursementLimit_Info, ReimbursementLimit_TBody } from './TanStackParts/TanStackComponent'

const X_DEBUG_TOGGLE = false
const X_DEBUG = process.env.REACT_APP_ENV === 'development' && X_DEBUG_TOGGLE

export type CRMColumnDef<TData> = ColumnDef<TData> & {
  excelColumnHidden?: boolean
}

export interface MasterDataTableTanstack_CompanyCustom {
  maspion: {
    totalDpp: number | null
    totalPPN: number | null
    totalJumlah: number | null
  }
}

export interface FilterConfigTanstack {
  excludeGlobalFilter?: string[]
}

interface Props {
  tableKey: string
  tableColumns: any
  actions?: any
  expanded?: boolean
  search?: boolean
  apiURL?: string
  visibleColumns?: string[] | null
  pinnedColumns?: {
    left?: string[]
    right?: string[]
    top?: string[]
    bottom?: string[]
  }
  autoReload: boolean
  customParams?: any
  dateStart?: string
  dateEnd?: string
  onRowClicked?: (row: any, event?: any) => void
  orderType?: 'ASC' | 'DESC'
  orderBy?: string
  currency?: string
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>
  showPagination?: boolean
  filterConfig?: FilterConfigTanstack
  tableStyle?: CSSProperties & {
    size?: 'SMALL' | 'MEDIUM'
  }
  tableHeaderStyle?: CSSProperties
  tableBodyStyle?: CSSProperties
  tableRowStyle?: CSSProperties
  tableFooterStyle?: CSSProperties
}

const MasterDataTableTanstack: FC<Props> = ({
  tableKey,
  tableColumns,
  actions,
  visibleColumns,
  pinnedColumns = {
    left: ['action'],
  },
  apiURL,
  search = true,
  autoReload,
  customParams,
  dateStart,
  dateEnd,
  onRowClicked,
  orderType = 'DESC',
  orderBy = 'dateCreated',
  currency,
  setLoading,
  showPagination = true,
  filterConfig,
  tableStyle = {
    size: 'MEDIUM',
    fontSize: '12px',
    width: '100%',
  },
  tableHeaderStyle,
  tableBodyStyle,
  tableRowStyle = {
    minWidth: '80px',
    width: '100%',
  },
  tableFooterStyle,
}) => {
  const BASE_URL = process.env.REACT_APP_API_URL
  const token = useAccessToken()
  const user = useUser()
  const {companyId} = useCustomeStore()
  const [companyCustom, setCompanyCustom] = useState<MasterDataTableTanstack_CompanyCustom>({
    maspion: {
      totalDpp: null,
      totalPPN: null,
      totalJumlah: null,
    },
  })
  const {templatePipelineId} = useParams()
  const {pipelines, selectMultiPipeline, pipelineLoading} = usePipelineStore()
  const {merchants, merchantLoading, selectMultiMerchant} = useMerchantStore()
  const {users, userLoading} = useUserStore()
  const {settings} = useSettingsStore()
  const {
    tablesData,
    setTablesData,
    selectedFilters,
    filteredTablesData,
    setFilteredTablesData,
    setIsLoadingTable,
    setIsValidatingTable,
    setIsFullyLoaded,
    masterDateRange,
    tableColumnVisibility,
    setTableColumnVisibility
  } = useDataTableStore()
  const intl = useIntl()
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: pinnedColumns.left || [],
    right: pinnedColumns.right || [],
  })
  const [globalFilter, setGlobalFilter] = useState<string>('')
  const [expandedRows, setExpandedRows] = useState<ExpandedState>({})
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const fetcher = (url: string) => {
    let params = {}
    setIsFullyLoaded(false);

    if (tableKey === 'dashboard-leads-report' || tableKey === 'merchants') {
      params = {
        dateStartedFrom: dateStart,
        dateStartedTo: dateEnd,
      }
    }
    if (tableKey === 'reimbursement-limits') {
      params = {
        currencyId: currency,
        periodStartFrom: dateStart,
        periodStartTo: dateEnd,
      }
    }
    if (tableKey === 'list/invoices') {
      params = {
        createdDateFrom: dateStart,
        createdDateTo: dateEnd,
      }
    }

    return axios
      .get(url, {
        headers: {
          'X-Auth-token': token,
        },
        params,
      })
      .then((res) => {
        setIsFullyLoaded(true);
        return res.data.data
      })
  }

  const {
    data = [],
    error,
    isLoading,
    isValidating,
  } = useSWR(apiURL ? BASE_URL + apiURL : null, fetcher, {
    revalidateOnFocus: autoReload,
    revalidateOnReconnect: autoReload,
  })

  useEffect(() => {
    setIsLoadingTable(tableKey, isLoading)
    setIsValidatingTable(tableKey, isValidating)
  }, [isLoading, isValidating])

  useEffect(() => {
    // console.log('tanstack-data', data)
    // const stressTestData = Array(9).fill(data).flat()
    if (!isLoading && !merchantLoading && !pipelineLoading && !userLoading) {
      if (data) {
        if (tableKey === 'dashboard-leads-report') {
          const result: DashboardLeadsReportTable[] = []

          const calculatePerformance = (
            obj: any,
            desc: string,
            prospectName: string,
            targetPaid: number,
            targetProspect: number
          ) => {
            let total =
              obj.dropLeadsCount + obj.coldLeadsCount + obj.hotLeadsCount + obj.paidLeadsCount
            let lostPercent = (obj.dropLeadsCount / total) * 100
            let coldPercent = (obj.coldLeadsCount / total) * 100
            let hotPercent = (obj.hotLeadsCount / total) * 100
            let paidPercent = (obj.paidLeadsCount / total) * 100
            lostPercent = isNaN(lostPercent) ? 0 : lostPercent
            coldPercent = isNaN(coldPercent) ? 0 : coldPercent
            hotPercent = isNaN(hotPercent) ? 0 : hotPercent
            paidPercent = isNaN(paidPercent) ? 0 : paidPercent
            let totalPercent = lostPercent + coldPercent + hotPercent + paidPercent

            let prospectAchievementPercent = (obj.leadsCount / targetProspect) * 100
            let paidAchievementPercent = (obj.paidLeadsCount / targetPaid) * 100

            return {
              desc,
              prospect: prospectName,
              prospectTarget: targetProspect,
              prospectAchievementQ: obj.leadsCount,
              prospectAchievementPercent: isNaN(prospectAchievementPercent)
                ? 0
                : Math.round(prospectAchievementPercent * 100) / 100,
              paidTarget: targetPaid,
              paidAchievementQ: obj.paidLeadsCount,
              paidAchievementPercent: isNaN(paidAchievementPercent)
                ? 0
                : Math.round(paidAchievementPercent * 100) / 100,
              lostQ: obj.dropLeadsCount,
              lostPercent: Math.round(lostPercent * 100) / 100,
              coldQ: obj.coldLeadsCount,
              coldPercent: Math.round(coldPercent * 100) / 100,
              hotQ: obj.hotLeadsCount,
              hotPercent: Math.round(hotPercent * 100) / 100,
              paidQ: obj.paidLeadsCount,
              paidPercent: Math.round(paidPercent * 100) / 100,
              totalQ: total,
              totalPercent: Math.round(totalPercent * 100) / 100,
            }
          }

          result.push(
            ...Object.values(data.usersPerformance).map((obj: any) =>
              calculatePerformance(
                obj,
                user.data.is_superadmin ? 'Total Cabang' : 'Total Sales',
                obj.ownerName,
                obj.ownerName.includes('DUIN-TU') ? 15 : 5,
                75
              )
            )
          )

          setTablesData(tableKey, result)
          setFilteredTablesData(tableKey, result)
        } else if (tableKey === 'paid-schedule-termin') {
          let detail = Object.keys(data.dataTermin).map((key: string, index: number) => ({
            no: index + 1,
            dpp: Math.ceil(data.dataTermin[key].dpp),
            keterangan: data.dataTermin[key].keterangan,
            ppn: Math.ceil(data.dataTermin[key].ppn),
            tanggalJatuhTempo: moment(data.dataTermin[key].tanggalJatuhTempo).format(
              'DD MMMM YYYY'
            ),
            tanggalPembayaran: data.dataTermin[key].tanggalPembayaran
              ? moment(data.dataTermin[key].tanggalPembayaran).format('DD MMMM YYYY')
              : '-',
            total: Math.ceil(data.dataTermin[key].total),
          }))

          customParams.setDataPaidScheduleTermin({...data, detail})
          setTablesData(tableKey, detail)
          setFilteredTablesData(tableKey, detail)
        } else if (tableKey === 'reimbursement-limits') {
          const result: any[] = []
          result.push(
            ...Object.values(data).map((obj: any) => {
              return {
                userName: obj.userName,
                roleName: obj.roleName,
                userId: obj.userId,
                ...transformDataLimits(obj.dataYearly, obj.currency, obj.userId, obj.userName),
              }
            })
          )
          setTablesData(tableKey, result)
          setFilteredTablesData(tableKey, result)
          if (setLoading) {
            setLoading(false)
          }
        } else if (
          tableKey === 'merchants' ||
          tableKey === 'products' ||
          tableKey === 'labels' ||
          tableKey === 'units' ||
          tableKey === 'lead_sources' ||
          tableKey === 'lead_progresses' ||
          tableKey === 'conferences' ||
          tableKey === 'emails' ||
          tableKey === 'roles' ||
          tableKey === 'invoices' ||
          tableKey === 'log/invoices' ||
          tableKey === 'list/invoices'
        ) {
          handleFormatData({
            tableKey,
            data,
            orderBy,
            orderType,
            setLoading,
            intl,
            user,
            companyId,
            selectMultiPipeline,
            selectMultiMerchant,
            pipelines,
            merchants,
            users,
            settings,
          }).then((result) => {
            if (result) {
              setTablesData(tableKey, result.sortedData)
              setFilteredTablesData(tableKey, result.sortedData)
            } else {
              setTablesData(tableKey, [data])
              setFilteredTablesData(tableKey, [data])
            }
          })
        }
      }
    }
  }, [data, merchantLoading, pipelineLoading, userLoading, selectMultiMerchant, orderBy, orderType])

  useEffect(() => {
    if (apiURL && dateStart && dateEnd) {
      mutate(BASE_URL + apiURL)
    }
  }, [dateStart, dateEnd, customParams]);

  const table = useReactTable({
    // data: (filteredTablesData[tableKey] || []).concat(...Array(50).fill(filteredTablesData[tableKey] || [])), // ⚠️ stress test
    data: filteredTablesData[tableKey] || [],
    columns: tableColumns,
    state: {
      sorting,
      globalFilter,
      pagination,
      expanded: expandedRows,
      columnVisibility: columnVisibility || {},
      columnPinning,
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onExpandedChange: setExpandedRows,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    globalFilterFn: customGlobalFilter(filterConfig),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false,
  })

  const getAllRows = useMemo(() => table.getRowModel().rows, [table])

  useEffect(() => {
    
    setCompanyCustom({
      maspion: {
        totalDpp: calculateTotal(getAllRows, 'dpp'),
        totalPPN: calculateTotal(getAllRows, 'ppn'),
        totalJumlah: calculateTotal(getAllRows, 'total'),
      },
    })
  }, [getAllRows])

  // ================================== CONFIG: Table Filter ==================================
  useEffect(() => {
    const baseData = tablesData[tableKey]
    let filteredData = baseData

    // -------------------------------- Global Filter Search Input --------------------------------
    // if (globalFilter) {
    //   filteredData = filteredData.filter((row) => {
    //     /*====*/ X_DEBUG && console.log('tableKey:', tableKey, 'tableColumns:', tableColumns)
    //     /*====*/ X_DEBUG && console.log('globalFilter',globalFilter)
    //     if (
    //       filterConfig
    //     ) {
    //       /*====*/ X_DEBUG && console.log('filterConfig',filterConfig)
    //       const searchIndex = [...tableColumns, '']
    //       // console.log('tableColumns', tableColumns)
    //       // return tableColumns.some((column: any) => {
    //       //   const value = row[column.id];
    //       //   console.log('row', row)
    //       //   return String(value).toLowerCase().includes(globalFilter.toLowerCase());
    //       // });
    //       const listExludeGlobalFilter = filterConfig.excludeGlobalFilter ?? []
    //       return Object.entries(row)
    //       .filter(([key]) => listExludeGlobalFilter.includes(key))
    //       .flatMap(([_, value]) => [value])
    //       .some((value) => String(value).toLowerCase().includes(globalFilter.toLowerCase()));
    //     }
    //     else {
    //       return Object.values(row)
    //       .flat()
    //       .some((value) => String(value).toLowerCase().includes(globalFilter.toLowerCase()));
    //     }
    //   });
    // }

    // -------------------------------- CONFIG: Table Column Filter --------------------------------
    if (tableColumnVisibility[tableKey]) {
      /*====*/ X_DEBUG && console.log('tableColumnVisibility[tableKey]', tableColumnVisibility[tableKey])
      // const newColumnVisibility = tableColumns.reduce(
      //   (acc: Record<string, boolean>, column: any) => {
      //     acc[column.id] = tableColumnVisibility[tableKey][column.id] ?? false;
      //     return acc
      //   },
      //   {} as Record<string, boolean>
      // )
      const filteredVisibleColumns = Object.fromEntries(
        tableColumns.map((column: any) => [
          column.id,
          tableColumnVisibility[tableKey][column.id] ?? false,
        ])
      );
      setColumnVisibility(filteredVisibleColumns)
    }

    // -------------------------------- Date Range Filter --------------------------------
    if (masterDateRange && tableKey in masterDateRange) {
      /*====*/ X_DEBUG && console.log('masterDateRange', masterDateRange)
      const dateRange = masterDateRange[tableKey as keyof typeof masterDateRange]
      const startDate = dateRange ? new Date(dateRange.startDate) : null
      const endDate = dateRange ? new Date(dateRange.endDate) : null

      if (startDate && endDate) {
        filteredData = filteredData.filter((item) => {
          const dateCreated = new Date(item.dateCreated)
          return dateCreated >= startDate && dateCreated <= endDate
        })
      }
    }

    // -------------------------------- Filter by selected filters --------------------------------
    if (selectedFilters && selectedFilters[tableKey]) {
      /*====*/ X_DEBUG && console.log('selectedFilters', selectedFilters)
      if (selectedFilters?.[tableKey]) {
        Object.keys(selectedFilters[tableKey]).forEach((filterKey) => {
          const filterValues = selectedFilters[tableKey][filterKey]
          if (Array.isArray(filterValues) && filterValues.length > 0) {
            filteredData = filteredData.filter((item) => {
              if (Array.isArray(item[filterKey])) {
                return item[filterKey].some((subItem: any) => filterValues.includes(subItem.name))
              }
              return filterValues.includes(item[filterKey])
            })
          }
        })
      }
    }
    // -------------------------------- Reset Filter by selected filters --------------------------------
    if (!masterDateRange && (!globalFilter || globalFilter.trim() === '') && !selectedFilters) {
      filteredData = baseData
    }

    if (
      selectedFilters['reimbursement-limits']?.userId?.length > 0 &&
      tableKey === 'reimbursement-limits'
    ) {
      filteredData = filteredData.filter((item) =>
        selectedFilters['reimbursement-limits'].userId.includes(item.userId)
      )
    }

    setFilteredTablesData(tableKey, filteredData)
  }, [tableColumnVisibility, masterDateRange, globalFilter, selectedFilters, tablesData, tableKey])

  useEffect(() => {
    setGlobalFilter('')
  }, [apiURL])

  // ------------------------------- Debug -------------------------------

  // useEffect(() => {
  //   console.log('filteredTablesData[tableKey]',filteredTablesData[tableKey])
  // }, [filteredTablesData[tableKey]]);

  // useEffect(() => {
  //   console.log('tanstack-selectedFilters', selectedFilters)
  // }, [selectedFilters]);

  return (
    <div className='card rounded'>
      {(isLoading || isValidating) && (
        <div className='loading-overlay'>
          <div className='loader'></div>
        </div>
      )}
      <div className='card-header border-0'>
        {search && (
          <div className='card-title'>
            <div className='d-flex align-items-center position-relative my-1'>
              <span className='svg-icon svg-icon-1 position-absolute ms-6'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <rect
                    opacity='0.5'
                    x='17.0365'
                    y='15.1223'
                    width='8.15546'
                    height='2'
                    rx='1'
                    transform='rotate(45 17.0365 15.1223)'
                    fill='black'
                  />
                  <path
                    d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                    fill='black'
                  />
                </svg>
              </span>
              <input
                type='search'
                className='form-control form-control-solid w-250px ps-14'
                placeholder={intl.formatMessage({id: 'MASTERS.DATATABLE.SEARCH.PLACEHOLDER'})}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
          </div>
        )}
        <div className='card-toolbar align-self-center'>
          {actions &&
            actions.map((action: JSX.Element, index: number) => (
              <div key={index} className='me-5 me-md-3 d-flex align-items-baseline '>
                {action}
              </div>
            ))}
        </div>
      </div>
      <div className='card-body pt-0'>
        {tableKey === 'reimbursement-limits' && (
          <ReimbursementLimit_Info />
        )}
        <div className='table-responsive '>
          <table
            className={`table table-hover align-middle ${
              tableStyle.size === 'SMALL' ? 'table-sm' : ''
            }`}
            style={
              {
                ...tableStyle,
              } as React.CSSProperties
            }
          >
            {/* ------------------------------------- Thead ------------------------------------- */}
            <CRM_Thead tableKey={tableKey} table={table} tableHeaderStyle={tableHeaderStyle} />
            {/* ------------------------------------- Tbody ------------------------------------- */}
            {tableKey === 'reimbursement-limits' ? (
              <ReimbursementLimit_TBody table={table} />
            ) : (
              <CRM_Tbody tableKey={tableKey} table={table} tableBodyStyle={tableBodyStyle} onRowClicked={onRowClicked} />
            )}
          </table>
        </div>
        {showPagination && (
          <>
            <CRM_Pagination table={table} />
            <CustomCompanyView companyCustom={companyCustom} />
          </>
        )}
      </div>
    </div>
  )
}

export {MasterDataTableTanstack}